import type { IconName } from "@similarweb/icons";

import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { getIconForSignalType } from "app/pages/sales-intelligence/modules/signals/constants/signalTypeIcons";
import {
    requestAppsSignalsAccess,
    requestIntentSignalsAccess,
} from "app/pages/sales-intelligence/modules/signals/services/requestSignalTypeAccess";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import { AssetsService } from "app/services/AssetsService";

export type SignalHook = {
    readonly signalType: SignalType;
    /** Overview signals card -- a tile among the signal tiles. */
    readonly tileTitleKey: string;
    readonly tileDescriptionKey: string;
    /** Sales signals feed -- a full-width row inside the feed. */
    readonly bannerTitleKey: string;
    readonly bannerSubtitleKey: string;
    readonly iconImagePath?: string;
    readonly iconName?: IconName;
    readonly requestAccess: VoidFunction;
    /** Set when `requestAccess` is a silent sales request, which the surface must acknowledge itself. */
    readonly showsRequestSentState?: boolean;
};

/**
 * Order is precedence. Both company-analysis surfaces have a single upsell slot, so an account locked
 * out of more than one type sees the first entry only -- intent ahead of apps, per SI-9082. The keys are
 * listed rather than generated because intent's were published before the per-type stem existed.
 */
const signalHooks: readonly SignalHook[] = [
    {
        signalType: signalTypeMap.intent,
        tileTitleKey: "si.signals.intent-upgrade.banner.title",
        tileDescriptionKey: "si.signals.intent-upgrade.banner.description",
        bannerTitleKey: "si.signals.banner.intent-upsell.title",
        bannerSubtitleKey: "si.signals.banner.intent-upsell.subtitle",
        iconImagePath: AssetsService.assetUrl("/images/intent/hot_locked.svg"),
        requestAccess: requestIntentSignalsAccess,
    },
    {
        signalType: signalTypeMap.apps,
        tileTitleKey: "si.signals.apps-upgrade.banner.title",
        tileDescriptionKey: "si.signals.apps-upgrade.banner.description",
        bannerTitleKey: "si.signals.banner.apps-upsell.title",
        bannerSubtitleKey: "si.signals.banner.apps-upsell.subtitle",
        iconName: getIconForSignalType(signalTypeMap.apps),
        requestAccess: requestAppsSignalsAccess,
        showsRequestSentState: true,
    },
];

export const resolvePrimarySignalHook = (): SignalHook | undefined => {
    const lockedSignalTypes = new Set<SignalType>(signalsService.getLockedSignalTypes());

    return signalHooks.find(({ signalType }) => lockedSignalTypes.has(signalType));
};
